import { Injectable } from '@angular/core';
import { Affiliate, Organization, ParseObject, Query, Role, User } from '@telespot/sdk';
import { environment } from '@telespot/shared/environment';
import { LoggerService } from '@telespot/shared/logger/feature/util';
import { BehaviorSubject, defer, from, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly _currentOrganization = new BehaviorSubject<Organization>(this.getCurrentOrganization());
  private readonly _organizationsB$ = new BehaviorSubject<Organization[]>([]);
  private readonly _currentUserB$ = new BehaviorSubject<User>(this.currentUser);

  public readonly currentOrganization$ = this._currentOrganization.asObservable();
  public readonly organizations$ = this._organizationsB$.asObservable();
  public readonly currentUser$ = this._currentUserB$.asObservable();

  public redirectUrl: string;

  get currentOrganizationValue() {
    return this._currentOrganization.value;
  }

  constructor(private _logger: LoggerService) {
    this.updateUserRolesAndOrganizations();
  }

  private getCurrentOrganization(): Organization {
    const orgString = localStorage.getItem('currentOrganization');

    if (!orgString) return null;

    const orgJSON = { ...JSON.parse(orgString), className: 'Organization' };

    return Organization.fromJSON(orgJSON, false) as Organization;
  }

  private async getUserMetadata(parseUser: User): Promise<User> {
    let user = User.fromParseUser(parseUser);
    user.roles = await this.getUserRoles(user);
    user = await this.getUserOrganizations(user);

    localStorage.removeItem('currentUserRoles');
    localStorage.removeItem('currentUserAffiliations');
    localStorage.setItem('currentUserRoles', JSON.stringify(user.roles.map((r) => r.toJSON())));
    localStorage.setItem('currentUserAffiliations', JSON.stringify(user.affiliations.map((a) => a.toJSON())));

    this._currentUserB$.next(user);

    return user;
  }

  private async getUserOrganizations(user: User): Promise<User> {
    const query = new Query(Affiliate).equalTo('user', user).include('organization', 'user');
    const affiliates = await query.find();

    const organizations = await ParseObject.fetchAllWithInclude(
      affiliates.map((affiliate) => affiliate.organization),
      'license'
    );

    const organizationMap = new Map(organizations.map((org) => [org.id, org]));

    affiliates.forEach((affiliate) => {
      const updatedOrg = organizationMap.get(affiliate.organization.id);
      if (!updatedOrg) return;
      affiliate.set('organization', updatedOrg);
    });

    const sortedAffiliations = affiliates.sort((a, b) => a.organization.name.localeCompare(b.organization.name));

    this._organizationsB$.next(sortedAffiliations.map((a) => a.organization));

    user.affiliations = sortedAffiliations;
    return user;
  }

  get sessionToken(): string {
    const key = `Parse/${environment.api.appId}/currentUser`;
    return JSON.parse(localStorage.getItem(key))?.sessionToken;
  }

  get currentUser(): User {
    const roles = JSON.parse(localStorage.getItem('currentUserRoles') || '[]');
    const affiliations = JSON.parse(localStorage.getItem('currentUserAffiliations') || '[]');

    if (User.current() && !!roles?.length && !!affiliations?.length) {
      const current = User.fromParseUser(User.current());

      current.roles = roles.map((r) => Role.fromJSON({ ...r, className: '_Role' }, false));
      current.affiliations = affiliations.map((a) => Affiliate.fromJSON({ ...a, className: 'Affiliate' }, false));

      return current;
    } else return undefined;
  }

  get currentUserRoleNames(): string[] {
    return this.currentUser?.roles?.map((r) => r.getName()) || [];
  }

  public hasRoles(roles: string[]): boolean {
    return this.currentUserRoleNames.some((r) => roles.includes(r));
  }

  public setOrganization(organization: Organization) {
    const orgString = JSON.stringify(organization.toJSON());
    localStorage.setItem('currentOrganization', orgString);
    this._currentOrganization.next(organization);
  }

  public async setOrganizationFromId(organizationId: string) {
    const query = new Query(Organization).equalTo('objectId', organizationId);
    const organization = await query.first();
    this.setOrganization(organization);
  }

  public become(token: string): Observable<User> {
    return from(User.become(token).then((user: User) => this.getUserMetadata(user)));
  }

  public async login(username: string, password: string): Promise<User> {
    const emailRegex = /.*@.*\.\w+\b/gi;
    return User.logIn(emailRegex.test(username) ? username.toLowerCase() : username, password).then((user: User) =>
      this.getUserMetadata(user)
    );
  }

  public logout(): Observable<User | unknown> {
    return defer(() =>
      User.logOut().catch(() => {
        this._logger.debug(`Already logged off`);
      })
    ).pipe(tap(() => this.cleanUserSession()));
  }

  public cleanUserSession(): void {
    localStorage.removeItem(`Parse/${environment.api.appId}/currentUser`);
    localStorage.removeItem(`Parse/${environment.api.appId}/installationId`);
    localStorage.removeItem('currentUserRoles');
    localStorage.removeItem('currentUserAffiliations');
    localStorage.removeItem('currentOrganization');
    this._currentUserB$.next(undefined);
    this._currentOrganization.next(undefined);
    this._organizationsB$.next([]);
  }

  public getUserRoles(user: User): Promise<Role[]> {
    const query = new Query(Role).containedIn('users', [user]);
    return query.find();
  }

  public async updateUserRolesAndOrganizations(): Promise<void> {
    if (!this.currentUser) return;
    await this.getUserMetadata(this.currentUser);
  }
}
